// convert-column-definitions.ts
import { Change } from "@adornis/base/change.js";
import { isGQLScalarType } from "@adornis/baseql/baseqlTypes.js";
import { baseqlMetaData } from "@adornis/baseql/metadata/metaDataStore.js";
import { getAllFieldDefinitions } from "@adornis/baseql/metadata/resolve.js";
import { BaseQLUnion } from "@adornis/baseql/metadata/union.js";
var createSelectionSetFromDotPaths = (dotPaths) => {
  const result = {};
  for (const path in dotPaths) {
    const split = path.split(".").slice(0, -1);
    const last = path.split(".").slice(-1)[0];
    if (!last)
      throw new Error(`invalid path "${path}"`);
    let current = result;
    for (const pathPart of split) {
      if (!current[pathPart] || typeof current[pathPart] === "number")
        current[pathPart] = {};
      current = current[pathPart];
    }
    current[last] = dotPaths[path];
  }
  return result;
};
var convertColumnDefinitionsToSelectionSet = (entity, columnDefinitions) => {
  return columnDefinitions.reduce((acc, curr) => {
    if (!curr.selectionSet && curr.fieldName && typeof Change.resolvePath(acc, curr.fieldName.split(".")) === "object")
      return acc;
    const additionalSelection = curr.selectionSet ?? generateAdditionalSelectionForFieldName(entity, curr.fieldName);
    return Change.deepMerge(acc, additionalSelection);
  }, {});
};
var generateDotSyntaxFieldDefinitionsFromEntity = (entity, fieldBlacklist = [], fieldWhitelist = [], remainingDepth = 3, currentPrefix = "") => {
  if (remainingDepth === 0)
    return {};
  if (fieldBlacklist.length && fieldWhitelist.length)
    throw new Error("Cannot have blacklist AND whitelist");
  const fields = {
    ...Object.fromEntries(
      baseqlMetaData.entities.validSubclasses(entity).flatMap((subClass) => {
        const subClassEntityConstructor = baseqlMetaData.entities.resolveNode(subClass).type();
        return Object.entries(subClassEntityConstructor.fields);
      })
    ),
    ...entity.fields
  };
  return Object.entries(fields).reduce((acc, [key, value]) => {
    const newFieldPath = [currentPrefix, key].filter(Boolean).join(".");
    if (fieldBlacklist.length ? fieldBlacklist.some(
      (blacklistEntry) => typeof blacklistEntry === "string" ? blacklistEntry === newFieldPath : blacklistEntry.test(newFieldPath)
    ) : fieldWhitelist.length ? !fieldWhitelist.some(
      (whitelistEntry) => typeof whitelistEntry === "string" ? whitelistEntry === newFieldPath || whitelistEntry.startsWith(`${newFieldPath}.`) : whitelistEntry.test(newFieldPath)
    ) : false)
      return acc;
    if (!isGQLScalarType(value.type)) {
      const type = value.type();
      if (type instanceof BaseQLUnion) {
        const unionDef = baseqlMetaData.unions[type._class];
        let subEntity = {};
        for (const entity2 of unionDef?.types() ?? []) {
          subEntity = {
            ...subEntity,
            ...generateDotSyntaxFieldDefinitionsFromEntity(
              entity2,
              // somewhat wrong typing...
              fieldBlacklist,
              fieldWhitelist,
              remainingDepth - 1,
              newFieldPath
            )
          };
        }
        acc = { ...acc, [newFieldPath]: value, ...subEntity };
      } else {
        const subEntity = generateDotSyntaxFieldDefinitionsFromEntity(
          type,
          // somewhat wrong typing...
          fieldBlacklist,
          fieldWhitelist,
          remainingDepth - 1,
          newFieldPath
        );
        acc = { ...acc, [newFieldPath]: value, ...subEntity };
      }
    } else
      acc[newFieldPath] = value;
    return acc;
  }, {});
};
var generateAdditionalSelectionForFieldName = (entity, fieldName) => {
  if (!fieldName)
    return {};
  const path = fieldName.split(".");
  let entityType = entity;
  do {
    const field = path.shift();
    if (!field)
      throw new Error("will not happen!");
    const newType = getAllFieldDefinitions(entityType).find((curField) => curField.name === field);
    if (!newType)
      throw new Error(
        `we don't have a type called at "${field}" in "${entityType._class}", the selection string is wrong`
      );
    entityType = newType.type();
  } while (path.length);
  return createSelectionSetFromDotPaths({
    [fieldName]: isGQLScalarType(() => entityType) ? 1 : entityType.allFields
  });
};
export {
  convertColumnDefinitionsToSelectionSet,
  createSelectionSetFromDotPaths,
  generateAdditionalSelectionForFieldName,
  generateDotSyntaxFieldDefinitionsFromEntity
};
